const ACTION_SELECTOR =
  '[data-perf-action], button, a, input, textarea, select';

// Fallback labels when the nearest control has no data-perf-action. These are
// the attribute-based names the Datadog SDK reads for click actions, so the
// vital and RUM actions agree. Element text is never used.
const LABEL_SELECTOR = '[data-dd-action-name], [aria-label]';

export type InputActionSource = 'perf-action' | 'dd-action-name' | 'aria-label';

export type InputAction = {
  action: string | undefined;
  actionSource: InputActionSource | undefined;
};

/** Use the same nearest-control attribution for input lag and interaction windows. */
export function getInputActionElement(
  target: EventTarget | null
): Element | null {
  return target instanceof Element ? target.closest(ACTION_SELECTOR) : null;
}

/**
 * Resolve an action label for an input target. The stable data-perf-action on
 * the nearest control wins. Otherwise use the closest ancestor carrying a
 * data-dd-action-name or aria-label, walking from the original target so
 * labelled wrappers without a control inside (a chart canvas) still resolve.
 */
export function resolveInputAction(target: EventTarget | null): InputAction {
  const perfAction =
    getInputActionElement(target)?.getAttribute('data-perf-action');
  if (perfAction) {
    return {action: perfAction, actionSource: 'perf-action'};
  }
  const labelled =
    target instanceof Element ? target.closest(LABEL_SELECTOR) : null;
  const ddActionName = labelled?.getAttribute('data-dd-action-name');
  if (ddActionName) {
    return {action: ddActionName, actionSource: 'dd-action-name'};
  }
  const ariaLabel = labelled?.getAttribute('aria-label');
  if (ariaLabel) {
    return {action: ariaLabel, actionSource: 'aria-label'};
  }
  return {action: undefined, actionSource: undefined};
}
